import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ErrorService } from '../shared/services/error.service';

export interface StatusServiceOrder {
  id: number;
  name: string;
  configOrderId?: number | null;
}

export interface StatusServiceOrderCreate {
  name: string;
  configOrderId: number;
}

export interface StatusServiceOrderUpdate {
  name?: string;
  configOrderId?: number;
}

@Injectable({ providedIn: 'root' })
export class StatusServiceOrdersService {
  private readonly baseUrl = environment.microservices?.order || environment.apiUrl || '/api';
  private readonly resourcePath = 'statusserviceorders';

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    const headerMap: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headerMap['Authorization'] = `Bearer ${token}`;
    }

    return new HttpHeaders(headerMap);
  }

  private buildUrl(path: string): string {
    const [base, baseQuery] = String(this.baseUrl).split('?');
    const normalizedBase = base.replace(/\/+$/, '');
    const normalizedPath = String(path).replace(/^\/+/, '');
    let url = `${normalizedBase}/${normalizedPath}`;
    if (baseQuery) {
      url += `?${baseQuery}`;
    }
    return url;
  }

  /**
   * GET /statusserviceorders
   */
  getAll(): Observable<StatusServiceOrder[]> {
    return this.http.get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders() }).pipe(
      map(res => this.extractArray(res).map(dto => this.mapFromDto(dto))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao listar status de OS');
        return throwError(() => err);
      })
    );
  }

  /**
   * GET /statusserviceorders?configOrderId={configOrderId}
   */
  getByConfigOrderId(configOrderId: number): Observable<StatusServiceOrder[]> {
    const params = new HttpParams().set('configOrderId', String(configOrderId));
    return this.http.get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders(), params }).pipe(
      map(res => this.extractArray(res).map(dto => this.mapFromDto(dto))),
      catchError(err => {
        // Alguns backends retornam 404 quando não há itens; tratar como lista vazia.
        if (err?.status === 404) {
          return of([] as StatusServiceOrder[]);
        }

        this.errorService.showErrorMessage('Erro ao listar status de OS por configuração');
        return throwError(() => err);
      })
    );
  }

  /**
   * GET /statusserviceorders/{id}
   */
  getById(id: number): Observable<StatusServiceOrder> {
    return this.http.get<any>(this.buildUrl(`${this.resourcePath}/${id}`), { headers: this.getHeaders() }).pipe(
      map(res => this.mapFromDto(this.extractItem(res))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar status de OS');
        return throwError(() => err);
      })
    );
  }

  /**
   * POST /statusserviceorders
   */
  create(payload: StatusServiceOrderCreate): Observable<StatusServiceOrder> {
    const headers = this.getHeaders();

    return this.http.post<any>(this.buildUrl(this.resourcePath), payload, { headers }).pipe(
      map(res => this.mapFromDto(this.extractItem(res))),
      tap(() => this.errorService.showSuccessMessage('Status de OS criado com sucesso')),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao criar status de OS');
        return throwError(() => err);
      })
    );
  }

  /**
   * PUT /statusserviceorders/{id}
   */
  update(id: number, payload: StatusServiceOrderUpdate): Observable<StatusServiceOrder> {
    const headers = this.getHeaders();

    return this.http.put<any>(this.buildUrl(`${this.resourcePath}/${id}`), payload, { headers }).pipe(
      map(res => this.mapFromDto(this.extractItem(res))),
      tap(() => this.errorService.showSuccessMessage('Status de OS atualizado com sucesso')),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao atualizar status de OS');
        return throwError(() => err);
      })
    );
  }

  /**
   * DELETE /statusserviceorders/{id}
   */
  delete(id: number): Observable<void> {
    return this.http.delete<void>(this.buildUrl(`${this.resourcePath}/${id}`), { headers: this.getHeaders() }).pipe(
      map(() => void 0),
      tap(() => this.errorService.showSuccessMessage('Status de OS removido com sucesso')),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao remover status de OS');
        return throwError(() => err);
      })
    );
  }

  private extractArray(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data?.data)) return response.data.data;
    if (Array.isArray(response?.items)) return response.items;
    if (Array.isArray(response?.data?.items)) return response.data.items;
    if (Array.isArray(response?.statusServiceOrders)) return response.statusServiceOrders;
    if (Array.isArray(response?.StatusServiceOrders)) return response.StatusServiceOrders;
    if (Array.isArray(response?.data?.statusServiceOrders)) return response.data.statusServiceOrders;
    if (Array.isArray(response?.data?.StatusServiceOrders)) return response.data.StatusServiceOrders;
    return [];
  }

  private extractItem(response: any): any {
    return response?.data?.data ?? response?.data ?? response?.item ?? response?.statusServiceOrder ?? response;
  }

  private mapFromDto(dto: any): StatusServiceOrder {
    const rawId = dto?.Id ?? dto?.id ?? 0;
    const rawConfigOrderId = dto?.ConfigOrderId ?? dto?.configOrderId ?? null;

    const id = Number(rawId);
    const configOrderId = rawConfigOrderId == null ? null : Number(rawConfigOrderId);

    return {
      id: Number.isFinite(id) ? id : 0,
      name: dto?.Name ?? dto?.name ?? '',
      configOrderId: typeof configOrderId === 'number' && Number.isFinite(configOrderId) ? configOrderId : null
    };
  }
}
